import {
    api,
    ApiError,
    formatBps,
    formatDay,
    formatMonthYear,
    oneSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Banner,
    Button,
    DateTime,
    PageHeader,
    PublicId,
    ReasonDialog,
    RowActions,
    Section,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type {
    RiskMeasure,
    RiskMonitoringDetail,
    RiskPeriodFigures,
} from '@/api/types';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { RatioTrendChart } from '@/pages/risk/dashboard-charts';
import {
    LiftMeasureDialog,
    MEASURE_INVALIDATES,
    measureLabel,
    ProposeMeasureSheet,
} from '@/pages/risk/measures';

/** The reads of `/risk/monitoring/:merchant` (BM-46 §2.0). */
export const riskMonitoringReads = (merchant: string | undefined) => ({
    detail: oneSpec(`/staff/risk/monitoring/${merchant ?? ''}`),
});

const bps = (v: unknown) => (typeof v === 'number' ? formatBps(v) : '—');
const num = (v: unknown) => (typeof v === 'number' ? String(v) : '—');

function period(f: RiskPeriodFigures): string {
    return f.period_kind === 'month'
        ? formatMonthYear(f.period_start.slice(0, 7))
        : formatDay(f.period_start);
}

/** The figures rows: one per period and store, the ratios the PT rules read, and what they hit. */
function FiguresTable({ rows }: { rows: readonly RiskPeriodFigures[] }) {
    if (rows.length === 0)
        return (
            <p className="text-fg-2 m-0 text-sm">
                No figures yet: the first run is tonight after 02:00, or on the
                first of the month.
            </p>
        );
    return (
        <div className="overflow-x-auto">
            <Table data-testid="monitoring-figures">
                <TableHeader>
                    <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead>Store</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead className="text-right">Sales</TableHead>
                        <TableHead className="text-right">
                            Chargebacks
                        </TableHead>
                        <TableHead className="text-right">VAMP</TableHead>
                        <TableHead className="text-right">Fraud</TableHead>
                        <TableHead className="text-right">Declines</TableHead>
                        <TableHead className="text-right">Refunds</TableHead>
                        <TableHead>Rules hit</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((f) => (
                        <TableRow key={f.id}>
                            <TableCell className="whitespace-nowrap">
                                {period(f)}
                            </TableCell>
                            <TableCell>
                                {f.store_id ? (
                                    <PublicId id={f.store_id} />
                                ) : (
                                    'Merchant'
                                )}
                            </TableCell>
                            <TableCell>
                                <StatusBadge
                                    status={f.state}
                                    domain={ADMIN_STATUSES.riskMonitoring}
                                />
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {num(f.figures.settled_count)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {bps(f.figures.chargeback_ratio_bps)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {bps(f.figures.vamp_ratio_bps)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {bps(f.figures.fraud_value_bps)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {bps(f.figures.decline_ratio_bps)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {bps(f.figures.refund_ratio_bps)}
                            </TableCell>
                            <TableCell>
                                {f.hits.length === 0 ? (
                                    '—'
                                ) : (
                                    <ul className="m-0 list-none space-y-1 p-0">
                                        {f.hits.map((h) => (
                                            <li
                                                key={h.rule}
                                                className="flex flex-wrap items-center gap-2 text-sm"
                                            >
                                                <StatusBadge
                                                    status={h.level}
                                                    domain={
                                                        ADMIN_STATUSES.riskMonitoring
                                                    }
                                                />
                                                <span className="font-mono text-xs">
                                                    {h.rule}
                                                </span>
                                                <span className="text-fg-2">
                                                    {h.explanation}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

/**
 * BM-125 slice 3: one merchant's monitoring: the figures with the PT hits, the ratio trend against the network lines,
 * the measures (propose, lift), the PT rules at the merchant's rating, and the monitoring case (notes, close).
 */
export function RiskMonitoringDetailPage() {
    const { merchant = '' } = useParams();
    const can = useAuthStore((s) => s.can);
    const me = useMe();
    const path = riskMonitoringReads(merchant).detail.path;
    const query = useOne<RiskMonitoringDetail>(path);
    const [proposing, setProposing] = useState(false);
    const [proposed, setProposed] = useState<RiskMeasure | null>(null);
    const [lifting, setLifting] = useState<RiskMeasure | null>(null);
    const [closing, setClosing] = useState(false);
    const [closeKey, setCloseKey] = useState(() => crypto.randomUUID());
    const close = useWrite(
        async (body: { caseId: string; reason: string }) =>
            (
                await api().post(
                    `/staff/risk/cases/${body.caseId}/close`,
                    { reason: body.reason },
                    { idempotencyKey: closeKey },
                )
            ).data,
        MEASURE_INVALIDATES,
    );
    const proposer = can(P.riskMeasuresPropose);
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Monitoring', href: '/risk/monitoring' }]}
                {...detailTitle(
                    query,
                    query.data?.data.merchant_name,
                    merchant,
                    'Merchant',
                )}
                meta={
                    <>
                        <PublicId id={merchant} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
                primaryAction={
                    proposer ? (
                        <Button
                            onClick={() => setProposing(true)}
                            data-testid="propose-measure-open"
                        >
                            Propose a measure
                        </Button>
                    ) : null
                }
            />
            <DetailBody query={query} title="Merchant monitoring">
                {(d) => {
                    const months = d.figures
                        .filter((f) => f.period_kind === 'month' && !f.store_id)
                        .slice()
                        .reverse();
                    return (
                        <div className="space-y-4">
                            {d.payouts_paused ? (
                                <Banner
                                    tone="danger"
                                    title="Payouts are paused"
                                    data-testid="monitoring-paused"
                                >
                                    A monitoring rule reached its Action level.
                                    The pause ends only when staff lift it
                                    below.
                                </Banner>
                            ) : null}
                            {proposed ? (
                                <Banner
                                    tone="info"
                                    title="Sent to Approvals"
                                    data-testid="measure-proposed"
                                >
                                    {measureLabel(proposed)} waits for a
                                    decision in{' '}
                                    <Link to="/approvals">Approvals</Link>
                                    {proposed.self_approvable
                                        ? '; you may approve it yourself.'
                                        : '; another member decides it.'}
                                </Banner>
                            ) : null}
                            <Section
                                title="State"
                                description="The worst state of the merchant's and its stores' latest figures."
                            >
                                <div className="flex flex-wrap items-center gap-3">
                                    <StatusBadge
                                        status={d.state}
                                        domain={ADMIN_STATUSES.riskMonitoring}
                                    />
                                    {d.rating ? (
                                        <Badge>Rating {d.rating}</Badge>
                                    ) : null}
                                    <span className="text-fg-2 text-sm">
                                        {d.params.store_action_pauses_merchant
                                            ? 'A store at Action pauses the merchant'
                                            : 'A store at Action is watched only'}
                                    </span>
                                </div>
                            </Section>
                            <Section
                                title="Ratio trend"
                                description="Monthly ratios against the card networks' warning (0.65%) and breach (1%) lines."
                            >
                                <RatioTrendChart
                                    rows={months.map((f) => ({
                                        period_start: f.period_start,
                                        chargeback_ratio_bps:
                                            (f.figures.chargeback_ratio_bps as
                                                number | null) ?? null,
                                        vamp_ratio_bps:
                                            (f.figures.vamp_ratio_bps as
                                                number | null) ?? null,
                                        fraud_value_bps:
                                            (f.figures.fraud_value_bps as
                                                number | null) ?? null,
                                    }))}
                                    warningBps={65}
                                    breachBps={100}
                                />
                            </Section>
                            <Section
                                title="Measures"
                                action={
                                    can(P.riskMeasuresApprove) ? (
                                        <Link
                                            to="/approvals"
                                            className="text-sm hover:underline"
                                        >
                                            Approvals
                                        </Link>
                                    ) : null
                                }
                            >
                                {d.measures.length === 0 ? (
                                    <p className="text-fg-2 m-0 text-sm">
                                        No measures on this merchant.
                                    </p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <Table data-testid="monitoring-measures">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>
                                                        Measure
                                                    </TableHead>
                                                    <TableHead>State</TableHead>
                                                    <TableHead>
                                                        Reason
                                                    </TableHead>
                                                    <TableHead>
                                                        Proposed
                                                    </TableHead>
                                                    <TableHead>
                                                        Applied
                                                    </TableHead>
                                                    <TableHead>
                                                        Lifted
                                                    </TableHead>
                                                    <TableHead aria-label="Actions" />
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {d.measures.map((m) => (
                                                    <TableRow key={m.id}>
                                                        <TableCell>
                                                            {measureLabel(m)}
                                                            {m.automatic ? (
                                                                <span className="text-fg-2 block text-xs">
                                                                    Automatic
                                                                    (monitoring
                                                                    Action)
                                                                </span>
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell>
                                                            <StatusBadge
                                                                status={m.state}
                                                                domain={
                                                                    ADMIN_STATUSES.riskMeasure
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell className="max-w-[320px]">
                                                            {m.reason}
                                                            {m.lift_reason ? (
                                                                <span className="text-fg-2 block text-xs">
                                                                    Lifted:{' '}
                                                                    {
                                                                        m.lift_reason
                                                                    }
                                                                </span>
                                                            ) : null}
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            <DateTime
                                                                value={
                                                                    m.created_at
                                                                }
                                                            />
                                                            <span className="text-fg-2 block text-xs">
                                                                {who(
                                                                    m.proposed_by,
                                                                    me,
                                                                )}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            <DateTime
                                                                value={
                                                                    m.applied_at
                                                                }
                                                                empty="—"
                                                            />
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap">
                                                            <DateTime
                                                                value={
                                                                    m.lifted_at
                                                                }
                                                                empty="—"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {proposer &&
                                                            (m.state ===
                                                                'applied' ||
                                                                m.state ===
                                                                    'proposed') ? (
                                                                <RowActions
                                                                    name={measureLabel(
                                                                        m,
                                                                    )}
                                                                    items={[
                                                                        {
                                                                            label:
                                                                                m.state ===
                                                                                'applied'
                                                                                    ? 'Lift'
                                                                                    : 'Withdraw',
                                                                            onSelect:
                                                                                () =>
                                                                                    setLifting(
                                                                                        m,
                                                                                    ),
                                                                        },
                                                                    ]}
                                                                />
                                                            ) : null}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </Section>
                            <Section title="Figures">
                                <FiguresTable rows={d.figures} />
                            </Section>
                            <Section
                                title="Monitoring rules"
                                description={`The parameters at this merchant's rating${d.rating ? ` (${d.rating})` : ''}; levels are edited on the Rules page.`}
                            >
                                <div className="overflow-x-auto">
                                    <Table data-testid="monitoring-rules">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Rule</TableHead>
                                                <TableHead>Runs</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Levels</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {d.rules.map((r) => (
                                                <TableRow key={r.key}>
                                                    <TableCell>
                                                        <span className="font-mono text-xs">
                                                            {r.key}
                                                        </span>{' '}
                                                        {r.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        {r.frequencies.join(
                                                            ', ',
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge>
                                                            {r.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-fg-2 text-xs">
                                                        {Object.entries(
                                                            r.params,
                                                        )
                                                            .filter(
                                                                ([, v]) =>
                                                                    v !== null,
                                                            )
                                                            .map(
                                                                ([k, v]) =>
                                                                    `${k} ${String(v)}`,
                                                            )
                                                            .join(' · ')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </Section>
                            <Section
                                title="Monitoring case"
                                action={
                                    d.case?.state === 'open' && proposer ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                close.reset();
                                                setClosing(true);
                                            }}
                                            data-testid="case-close-open"
                                        >
                                            Close case
                                        </Button>
                                    ) : null
                                }
                            >
                                {d.case ? (
                                    <div className="space-y-3">
                                        <div className="flex flex-wrap items-center gap-3 text-sm">
                                            <StatusBadge
                                                status={d.case.state}
                                                domain={
                                                    ADMIN_STATUSES.riskMonitoringCase
                                                }
                                            />
                                            <PublicId id={d.case.id} />
                                            <span>{d.case.opened_reason}</span>
                                            <span className="text-fg-2">
                                                Opened{' '}
                                                <DateTime
                                                    value={d.case.opened_at}
                                                />
                                            </span>
                                        </div>
                                        <ol
                                            className="m-0 list-none space-y-2 p-0"
                                            data-testid="monitoring-notes"
                                        >
                                            {d.case.notes.map((n) => (
                                                <li
                                                    key={n.id}
                                                    className="border-border-1 border-l-2 pl-3 text-sm"
                                                >
                                                    <p className="m-0 whitespace-pre-line">
                                                        {n.body}
                                                    </p>
                                                    <p className="text-fg-2 m-0 text-xs">
                                                        {who(n.author, me)} ·{' '}
                                                        <DateTime
                                                            value={n.created_at}
                                                        />
                                                    </p>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                ) : (
                                    <p className="text-fg-2 m-0 text-sm">
                                        No monitoring case. A rule at Action or
                                        a proposed measure opens one.
                                    </p>
                                )}
                            </Section>
                            {closing && d.case ? (
                                <ReasonDialog
                                    open
                                    onOpenChange={(o) =>
                                        o ? null : setClosing(false)
                                    }
                                    title="Close the monitoring case"
                                    description="A case with a measure still proposed or applied cannot close: lift the measures first."
                                    confirmLabel="Close case"
                                    required
                                    busy={close.isPending}
                                    error={close.error}
                                    onConfirm={(reason) =>
                                        close.mutate(
                                            {
                                                caseId: d.case?.id ?? '',
                                                reason,
                                            },
                                            {
                                                onSuccess: () =>
                                                    setClosing(false),
                                                onError: (err) => {
                                                    if (ApiError.is(err))
                                                        setCloseKey(
                                                            crypto.randomUUID(),
                                                        );
                                                },
                                            },
                                        )
                                    }
                                />
                            ) : null}
                        </div>
                    );
                }}
            </DetailBody>
            {proposing ? (
                <ProposeMeasureSheet
                    merchantId={merchant}
                    onClose={() => setProposing(false)}
                    onProposed={(m) => {
                        setProposing(false);
                        setProposed(m);
                    }}
                />
            ) : null}
            {lifting ? (
                <LiftMeasureDialog
                    measure={lifting}
                    onClose={() => setLifting(null)}
                />
            ) : null}
        </div>
    );
}
